import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  IoIosCheckmarkCircle,
  IoIosArrowDown,
  IoIosArrowUp,
} from 'react-icons/io';
import { FiChevronDown, FiX, FiCheck, FiX as FiCross } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

// Typecast icon components for TypeScript compatibility
const IoIosCheckmarkCircleComponent =
  IoIosCheckmarkCircle as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

const IoIosArrowDownComponent =
  IoIosArrowDown as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

const IoIosArrowUpComponent = IoIosArrowUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiCrossComponent = FiCross as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface ApiSegment {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  rulesJson: any[];
}

interface TagWithTimestamp {
  name: string;
  createdAt: number;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  gender: string;
  address: string;
  segments: string[] | null;
  tags: string[] | null;
  contactStatus: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  deliveredCount: number | null;
  openCount: number | null;
  clickCount: number | null;
  bounceCount: number | null;
  lastActivityAt: string | null;
  lastEmailOpenedAt: string | null;
  lastClickAt: string | null;
  engagementScore: number | null;
}

interface SetContextProps {
  campaignTitle: string;
  setCampaignTitle: (value: string) => void;
  receiver: string;
  setReceiver: (value: string) => void;
  tags: string;
  setTags: (value: string) => void;
  subject: string;
  setSubject: (value: string) => void;
  senderName: string;
  setSenderName: (value: string) => void;
  senderEmail: string;
  setSenderEmail: (value: string) => void;
  selectedTemplateImg: string;
  handleBack: () => void;
  handleNext: () => void;
  showFullForm?: boolean;
  isEditMode?: boolean;
  templateId?: string;
  campaignId?: string;
  hasTemplateChanged?: boolean;
  showNextButton?: boolean;
  // New props for selection type and contacts
  selectionType: string;
  setSelectionType: (value: string) => void;
  selectedContacts: string[];
  setSelectedContacts: (value: string[] | ((prev: string[]) => string[])) => void;
}

// Second step of the campaign creation process
const SetContext: React.FC<SetContextProps> = ({
  campaignTitle,
  setCampaignTitle,
  receiver,
  setReceiver,
  tags,
  setTags,
  subject,
  setSubject,
  senderName,
  setSenderName,
  senderEmail,
  setSenderEmail,
  selectedTemplateImg,
  handleBack,
  handleNext,
  showFullForm = false,
  templateId,
  campaignId,
  isEditMode = false,
  hasTemplateChanged = false,
  showNextButton = false,
  selectionType,
  setSelectionType,
  selectedContacts,
  setSelectedContacts,
}) => {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isCheckingName, setIsCheckingName] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string>('');
  const [isNameAvailable, setIsNameAvailable] = useState<boolean>(false);

  // Store initial values when in edit mode
  const [initialValues] = useState({
    campaignTitle: isEditMode ? campaignTitle : '',
    receiver: isEditMode ? receiver : '',
    tags: isEditMode ? tags : '',
    subject: isEditMode ? subject : '',
    senderName: isEditMode ? senderName : '',
    senderEmail: isEditMode ? senderEmail : '',
  });

  // Refs for debouncing
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckedNameRef = useRef<string>('');

  // ==================== SEGMENTS STATE ====================
  const [allSegments, setAllSegments] = useState<ApiSegment[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [isSegmentDropdownOpen, setIsSegmentDropdownOpen] = useState(false);
  const [segmentSearchTerm, setSegmentSearchTerm] = useState('');
  const segmentDropdownRef = useRef<HTMLDivElement>(null);

  // ==================== TAGS STATE ====================
  const [allTags, setAllTags] = useState<TagWithTimestamp[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [tagValidationError, setTagValidationError] = useState('');
  const tagsDropdownRef = useRef<HTMLDivElement>(null);

  // ==================== CONTACTS STATE ====================
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [isContactsDropdownOpen, setIsContactsDropdownOpen] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const contactsDropdownRef = useRef<HTMLDivElement>(null);

  // ==================== FETCH SEGMENTS ====================
  const fetchSegments = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    setLoadingSegments(true);

    try {
      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments?page=0&size=100&type=STATIC`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const segments = data.content || [];
        const seenNames = new Set();
        const uniqueSegments = segments.filter((seg: ApiSegment) => {
          const duplicate = seenNames.has(seg.name);
          seenNames.add(seg.name);
          return !duplicate;
        });
        setAllSegments(uniqueSegments);
      }
    } catch (error) {
      // console.error('❌ Failed to fetch segments:', error);
      toast.error('Failed to load segments. Please try again.')
    } finally {
      setLoadingSegments(false);
    }
  }, []);

  // ==================== FETCH TAGS ====================
  const fetchTags = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    setLoadingTags(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const tagsData = await response.json();
        const tagsArray = Array.isArray(tagsData) ? tagsData : [];
        const uniqueTags = tagsArray.filter((tag: string, index: number, self: string[]) =>
          index === self.indexOf(tag)
        );
        const tagsWithTimestamps: TagWithTimestamp[] = uniqueTags.map((tag, index) => ({
          name: tag,
          createdAt: Date.now() - (uniqueTags.length - index) * 1000,
        }));

        tagsWithTimestamps.sort((a, b) => b.createdAt - a.createdAt);

        setAllTags(tagsWithTimestamps);
      }
    } catch (error) {
      // console.error('❌ Failed to fetch tags:', error);
      toast.error('Failed to load tags. Please try again.');
    } finally {
      setLoadingTags(false);
    }
  }, []);

  // ==================== FETCH CONTACTS ====================
  const fetchContacts = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    setLoadingContacts(true);

    try {
      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/contacts?size=1000&page=0&status=SUBSCRIBED`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const contacts = data.contacts?.content || [];
        setAllContacts(contacts);
      }
    } catch (error) {
      // console.error('❌ Failed to fetch contacts:', error);
      toast.error('Failed to load contacts. Please try again.');
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  // ==================== CREATE NEW TAG ====================
  const handleCreateTag = async () => {
    const trimmedTag = newTagName.trim();

    if (!trimmedTag) {
      toast.error('Please enter a tag name');
      return;
    }

    // Check if tag already exists (case-insensitive)
    const tagExists = allTags.some(
      existingTag => existingTag.name.toLowerCase() === trimmedTag.toLowerCase()
    );

    if (tagExists) {
      setTagValidationError('Tag already exists');
      return;
    }

    setTagValidationError('');

    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    setIsCreatingTag(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmedTag }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      toast.success(`Tag "${trimmedTag}" created successfully!`);

      const newTag: TagWithTimestamp = {
        name: trimmedTag,
        createdAt: Date.now(),
      };

      setAllTags(prev => [newTag, ...prev]);
      setSelectedTags(prev => [...prev, trimmedTag]);

      // Update the parent tags state
      setTags([...selectedTags, trimmedTag].join(', '));

      setIsAddingTag(false);
      setNewTagName('');

      // Refresh tags from server after 2 seconds
      setTimeout(() => {
        fetchTags();
      }, 2000);

    } catch (error: any) {
      // console.error('❌ Failed to create tag:', error);
      toast.error(error.message || 'Failed to create tag');
      fetchTags();
    } finally {
      setIsCreatingTag(false);
    }
  };

  // ==================== TOGGLE SEGMENT SELECTION ====================
  const toggleSegment = (segmentId: string) => {
    setSelectedSegments((prev: string[]) => {
      const newSelection = prev.includes(segmentId)
        ? prev.filter(id => id !== segmentId)
        : [...prev, segmentId];

      // Update the parent receiver state with selected segment IDs
      setReceiver(newSelection.join(', '));
      return newSelection;
    });
  };

  // ==================== REMOVE SEGMENT ====================
  const removeSegment = (segmentId: string) => {
    setSelectedSegments((prev: string[]) => {
      const newSelection = prev.filter(id => id !== segmentId);
      setReceiver(newSelection.join(', '));
      return newSelection;
    });
  };

  // ==================== TOGGLE TAG SELECTION ====================
  const toggleTag = (tagName: string) => {
    setSelectedTags((prev: string[]) => {
      const newSelection = prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName];

      // Update the parent tags state
      setTags(newSelection.join(', '));
      return newSelection;
    });
  };

  // ==================== REMOVE TAG ====================
  const removeTag = (tagName: string) => {
    setSelectedTags((prev: string[]) => {
      const newSelection = prev.filter(t => t !== tagName);
      setTags(newSelection.join(', '));
      return newSelection;
    });
  };

  // ==================== TOGGLE CONTACT SELECTION ====================
  const toggleContact = (contactId: string) => {
    setSelectedContacts((prev: string[]) => {
      const newSelection = prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId];

      // Update the parent receiver state with selected contact IDs
      setReceiver(newSelection.join(', '));
      return newSelection;
    });
  };

  // ==================== REMOVE CONTACT ====================
  const removeContact = (contactId: string) => {
    setSelectedContacts((prev: string[]) => {
      const newSelection = prev.filter(id => id !== contactId);
      setReceiver(newSelection.join(', '));
      return newSelection;
    });
  };

  // ==================== TOGGLE DROPDOWNS ====================
  const toggleSegmentDropdown = () => {
    setIsSegmentDropdownOpen(!isSegmentDropdownOpen);
    if (isTagsDropdownOpen) setIsTagsDropdownOpen(false);
    if (isContactsDropdownOpen) setIsContactsDropdownOpen(false);
  };

  const toggleTagsDropdown = () => {
    const newState = !isTagsDropdownOpen;
    if (!newState) {
      setIsAddingTag(false);
      setNewTagName('');
      setTagValidationError('');
    }
    setIsTagsDropdownOpen(newState);
    if (isSegmentDropdownOpen) setIsSegmentDropdownOpen(false);
    if (isContactsDropdownOpen) setIsContactsDropdownOpen(false);
  };

  const toggleContactsDropdown = () => {
    setIsContactsDropdownOpen(!isContactsDropdownOpen);
    if (isSegmentDropdownOpen) setIsSegmentDropdownOpen(false);
    if (isTagsDropdownOpen) setIsTagsDropdownOpen(false);
  };

  // ==================== FILTERED SEGMENTS ====================
  const filteredSegments = useMemo(() => {
    if (!segmentSearchTerm.trim()) return allSegments;
    const term = segmentSearchTerm.toLowerCase().trim();
    return allSegments.filter(seg =>
      seg.name.toLowerCase().includes(term) ||
      seg.id.toLowerCase().includes(term)
    );
  }, [allSegments, segmentSearchTerm]);

  // ==================== FILTERED TAGS ====================
  const filteredTags = useMemo(() => {
    if (!tagSearchTerm.trim()) return allTags;
    const term = tagSearchTerm.toLowerCase().trim();
    return allTags.filter(tag =>
      tag.name.toLowerCase().includes(term)
    );
  }, [allTags, tagSearchTerm]);

  // ==================== FILTERED CONTACTS ====================
  const filteredContacts = useMemo(() => {
    if (!contactSearchTerm.trim()) return allContacts;
    const term = contactSearchTerm.toLowerCase().trim();
    return allContacts.filter(contact =>
      contact.firstName.toLowerCase().includes(term) ||
      contact.lastName.toLowerCase().includes(term) ||
      contact.email.toLowerCase().includes(term)
    );
  }, [allContacts, contactSearchTerm]);

  // ==================== LOAD DATA ON MOUNT ====================
  useEffect(() => {
    fetchSegments();
    fetchTags();
    fetchContacts();
  }, [fetchSegments, fetchTags, fetchContacts]);

  // ==================== INITIALIZE SELECTED VALUES ====================
  useEffect(() => {
    // Initialize selected segments from receiver prop
    if (receiver && allSegments.length > 0) {
      const segmentIds = receiver.split(',').map(s => s.trim()).filter(Boolean);
      const validSegmentIds = segmentIds.filter(id =>
        allSegments.some(seg => seg.id === id)
      );
      if (validSegmentIds.length > 0) {
        setSelectedSegments(validSegmentIds);
        if (selectionType === '') {
          setSelectionType('segments');
        }
      }
    }

    // Initialize selected contacts from receiver prop
    if (receiver && allContacts.length > 0) {
      const contactIds = receiver.split(',').map(s => s.trim()).filter(Boolean);
      const validContactIds = contactIds.filter(id =>
        allContacts.some(contact => contact.id === id)
      );
      if (validContactIds.length > 0) {
        setSelectedContacts(validContactIds);
        if (selectionType === '') {
          setSelectionType('contactIds');
        }
      }
    }

    // Initialize selected tags from tags prop
    if (tags) {
      const tagNames = tags.split(',').map(t => t.trim()).filter(Boolean);
      if (tagNames.length > 0) {
        setSelectedTags(tagNames);
        if (selectionType === '') {
          setSelectionType('tags');
        }
      }
    }
  }, [receiver, tags, allSegments, allContacts, selectionType, setSelectionType, setSelectedContacts]);

  // ==================== CLICK OUTSIDE HANDLERS ====================
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (segmentDropdownRef.current && !segmentDropdownRef.current.contains(event.target as Node)) {
        setIsSegmentDropdownOpen(false);
        setSegmentSearchTerm('');
      }
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target as Node)) {
        setIsTagsDropdownOpen(false);
        setIsAddingTag(false);
        setNewTagName('');
        setTagValidationError('');
        setTagSearchTerm('');
      }
      if (contactsDropdownRef.current && !contactsDropdownRef.current.contains(event.target as Node)) {
        setIsContactsDropdownOpen(false);
        setContactSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ==================== HANDLE SELECTION TYPE CHANGE ====================
  const handleSelectionTypeChange = (type: string) => {
    setSelectionType(type);
    // Don't reset anything visually - just update the selection type
  };

  // Simple check: has anything changed?
  const hasChanges = useMemo(() => {
    if (!isEditMode) return false;

    return (
      campaignTitle !== initialValues.campaignTitle ||
      receiver !== initialValues.receiver ||
      tags !== initialValues.tags ||
      subject !== initialValues.subject ||
      senderName !== initialValues.senderName ||
      senderEmail !== initialValues.senderEmail ||
      hasTemplateChanged
    );
  }, [
    campaignTitle,
    receiver,
    tags,
    subject,
    senderName,
    senderEmail,
    initialValues,
    isEditMode,
    hasTemplateChanged,
  ]);

  // Toggles the visibility of a form section
  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Check if campaign name exists
  const checkCampaignNameExists = useCallback(async (name: string): Promise<boolean> => {
    if (!name.trim()) return false;

    try {
      setIsCheckingName(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        return false;
      }

      const response = await fetch(
        `${BASE_URL}/v1/campaign-wizard?page=0&size=1000`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const campaigns = data.content || [];

      const exists = campaigns.some((campaign: any) => {
        const isSameName = campaign.title.toLowerCase() === name.trim().toLowerCase();
        if (isEditMode && campaignId) {
          return isSameName && campaign.id !== campaignId;
        }
        return isSameName;
      });

      return exists;
    } catch (error) {
      // console.error('Error checking campaign name:', error);
      toast.error('Failed to check campaign name availability. Please try again.');
      return false;
    } finally {
      setIsCheckingName(false);
    }
  }, [isEditMode, campaignId]);

  // Debounced validation function
  const validateNameWithDebounce = useCallback((value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!value.trim()) {
      setNameError('');
      setIsNameAvailable(false);
      lastCheckedNameRef.current = '';
      return;
    }

    if (isEditMode && value.trim().toLowerCase() === initialValues.campaignTitle.trim().toLowerCase()) {
      setNameError('');
      setIsNameAvailable(false);
      lastCheckedNameRef.current = value.trim().toLowerCase();
      return;
    }

    if (lastCheckedNameRef.current === value.trim().toLowerCase()) {
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      const exists = await checkCampaignNameExists(value);

      if (exists) {
        setNameError('A campaign with this name already exists. Please choose a different name.');
        setIsNameAvailable(false);
      } else {
        setNameError('');
        setIsNameAvailable(true);
      }

      lastCheckedNameRef.current = value.trim().toLowerCase();
      debounceTimerRef.current = null;
    }, 500);
  }, [checkCampaignNameExists, isEditMode, initialValues.campaignTitle]);

  const handleTitleChange = (value: string) => {
    setCampaignTitle(value);
    validateNameWithDebounce(value);
  };

  // isDraftEnabled - segments no longer mandatory in edit mode
  const isDraftEnabled = useMemo(() => {
    if (showFullForm) {
      // Edit mode: only campaign title is mandatory
      if (isEditMode) {
        return (
          campaignTitle.trim().length > 0 &&
          !nameError &&
          !isCheckingName
        );
      } else {
        // Create mode: only campaign title is required
        return (
          campaignTitle.trim().length > 0 &&
          !nameError &&
          !isCheckingName
        );
      }
    } else {
      // Minimal form: only campaign title is required
      return campaignTitle.trim().length > 0 && !nameError && !isCheckingName;
    }
  }, [
    campaignTitle,
    showFullForm,
    isEditMode,
    nameError,
    isCheckingName,
  ]);

  const isUpdateEnabled = useMemo(() => {
    if (!isEditMode) return false;

    // Force disable update for 'all' and 'contactIds' selection types
    if (selectionType === 'all' || selectionType === 'contactIds') {
      return false;
    }

    return hasChanges && campaignTitle.trim().length > 0 && !nameError && !isCheckingName;
  }, [isEditMode, hasChanges, campaignTitle, nameError, isCheckingName, selectionType]);

  // Check if Next button should be enabled
  const isNextEnabled = useMemo(() => {
    if (!showNextButton) return false;

    // If selection type is empty (default "Select selection type"), disable Next
    if (selectionType === '') return false;

    // Check based on selection type
    if (selectionType === 'segments' && selectedSegments.length === 0) return false;
    if (selectionType === 'tags' && selectedTags.length === 0) return false;
    if (selectionType === 'contactIds' && selectedContacts.length === 0) return false;

    // For 'all' or any other, just check campaign title
    return campaignTitle.trim().length > 0 && !nameError && !isCheckingName;
  }, [showNextButton, selectionType, selectedSegments, selectedTags, selectedContacts, campaignTitle, nameError, isCheckingName]);

  // Segments are now optional in edit mode
  const isReceiverComplete = true;

  const isSubjectComplete = subject;
  // const isSenderComplete = senderName && senderEmail;

  // Handle Next button click with validation
  const handleNextWithValidation = () => {
    // If CONTACT_IDS is selected and no contacts are chosen, show toast
    if (selectionType === 'contactIds' && selectedContacts.length === 0) {
      toast.error('Please select at least one contact');
      return;
    }
    // Call the original handleNext
    handleNext();
  };

  // Handle Update Campaign with PATCH API
  const handleUpdateCampaign = async () => {
    if (!hasChanges) {
      toast.info('No changes to update');
      return;
    }

    if (!campaignTitle.trim()) {
      toast.error('Please enter a campaign title.');
      return;
    }

    // If selectionType is 'all' or 'contactIds', return early (should never happen since button is disabled)
    if (selectionType === 'all' || selectionType === 'contactIds') {
      toast.error('Update is not available for this selection type');
      return;
    }

    // Validation based on selection type
    if (selectionType === 'tags' && selectedTags.length === 0) {
      toast.error('At least 1 tag should be selected');
      return;
    }

    if (selectionType === 'segments' && selectedSegments.length === 0) {
      toast.error('At least 1 segment should be selected');
      return;
    }

    if (debounceTimerRef.current) {
      toast.info('Please wait while we validate the campaign name...');
      return;
    }

    // Check for duplicate name
    if (!(campaignTitle.trim().toLowerCase() === initialValues.campaignTitle.trim().toLowerCase())) {
      const exists = await checkCampaignNameExists(campaignTitle);
      if (exists) {
        toast.error('A campaign with this name already exists. Please choose a different name.');
        return;
      }
    }

    try {
      setIsSaving(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setIsSaving(false);
        return;
      }

      // PATCH API call
      const url = `${BASE_URL}/v1/campaign-wizard/${campaignId}`;

      // Determine what to send based on selection type
      let segmentIdsToSend: string[] = [];
      let tagIdsToSend: string[] = [];
      let contactIdsToSend: string[] = [];

      if (selectionType === 'segments') {
        segmentIdsToSend = selectedSegments;
        tagIdsToSend = [];
        contactIdsToSend = [];
      } else if (selectionType === 'tags') {
        segmentIdsToSend = [];
        tagIdsToSend = selectedTags;
        contactIdsToSend = [];
      } else {
        // For any other case, send both with their current values
        segmentIdsToSend = selectedSegments;
        tagIdsToSend = selectedTags;
        contactIdsToSend = selectedContacts;
      }

      // Request body with hardcoded senderProfileId
      const requestBody = {
        title: campaignTitle.trim(),
        subject: subject.trim() || '',
        templateId: templateId,
        senderProfileId: 'SPa9678533b0db42e1',
        segmentIds: segmentIdsToSend,
        tagIds: tagIdsToSend,
        // contactIds: contactIdsToSend,
      };



      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      // Handle 204 No Content response
      if (response.status === 204) {
        toast.success('Campaign updated successfully!');

        setTimeout(() => {
          navigate('/campaigns', { state: { refreshCampaigns: true } });
        }, 1500);
        return;
      }

      // Handle other responses
      if (!response.ok) {
        let errorMessage = `Failed to update campaign: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        // console.log('✅ Campaign updated successfully (no response body)');
      }

      toast.success('Campaign updated successfully!');

      setTimeout(() => {
        navigate('/campaigns', { state: { refreshCampaigns: true } });
      }, 1500);
    } catch (error: any) {
      // console.error('Error updating campaign:', error);
      toast.error(error.message || 'Failed to update campaign');
    } finally {
      setIsSaving(false);
    }
  };

  // Modified: Handle Save as Draft for create mode only
  const handleSaveAsDraft = async () => {
    // If edit mode, use update instead
    if (isEditMode) {
      await handleUpdateCampaign();
      return;
    }

    if (!isDraftEnabled) {
      return;
    }

    if (!templateId) {
      toast.error('No template selected. Please go back and select a template.');
      return;
    }

    if (!campaignTitle.trim()) {
      toast.error('Please enter a campaign title.');
      return;
    }

    if (debounceTimerRef.current) {
      toast.info('Please wait while we validate the campaign name...');
      return;
    }

    const exists = await checkCampaignNameExists(campaignTitle);
    if (exists) {
      toast.error('A campaign with this name already exists. Please choose a different name.');
      return;
    }

    try {
      setIsSaving(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setIsSaving(false);
        return;
      }

      const url = `${BASE_URL}/v1/campaign-wizard`;
      const method = 'POST';
      const requestBody = {
        templateId: templateId,
        title: campaignTitle.trim(),
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to save campaign: ${response.status}`
        );
      }

      const data = await response.json();
      toast.success('Campaign saved as draft successfully!');

      setTimeout(() => {
        navigate('/campaigns', { state: { refreshCampaigns: true } });
      }, 1500);
    } catch (error: any) {
      // console.error('Error saving campaign:', error);
      toast.error(error.message || 'Failed to save campaign as draft');
    } finally {
      setIsSaving(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const isOriginalName = isEditMode &&
    campaignTitle.trim().toLowerCase() === initialValues.campaignTitle.trim().toLowerCase();



  return (
    <>
      <div className="flex gap-6">
        <div className={showFullForm ? 'w-2/3' : 'w-full'}>
          <div className="space-y-4">
            {/* Campaign Title with Validation */}
            <div>
              <label
                className="block text-sm font-normal text-gray-700"
                style={{
                  color: '#3F5265',
                  fontSize: '12px',
                }}
              >
                CAMPAIGN TITLE <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={campaignTitle}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Enter campaign title"
                className={`mt-1 block w-full border rounded-lg p-2 ${nameError ? 'border-red-500' :
                  isNameAvailable && campaignTitle.trim().length > 0 && !isOriginalName ? 'border-green-500' :
                    campaignTitle.trim().length === 0 ? 'border-yellow-500' :
                      'border-gray-300'
                  }`}
                disabled={isSaving}
              />
              {isCheckingName && (
                <div className="text-sm text-gray-500 mt-1">
                  <span className="inline-block animate-pulse">Checking name availability...</span>
                </div>
              )}
              {nameError && (
                <div className="text-sm text-red-500 mt-1">
                  ⚠️ {nameError}
                </div>
              )}
              {!nameError && campaignTitle.trim().length > 0 && !isCheckingName && isNameAvailable && !isOriginalName && (
                <div className="text-sm text-green-500 mt-1">
                  ✓ Campaign name is available
                </div>
              )}
              {campaignTitle.trim().length === 0 && !isCheckingName && (
                <div className="text-sm text-yellow-500 mt-1">
                  ⚠️ Campaign title is required
                </div>
              )}
            </div>

            {showFullForm && (
              <>
                {/* Receiver Section with Multi-Select Segments and Tags */}
                <div
                  className={`border rounded-lg p-5 relative ${openSection === 'receiver' ? 'bg-[#E0F8FB]' : ''
                    }`}
                >
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleSection('receiver')}
                  >
                    <div className="flex items-center gap-2">
                      <IoIosCheckmarkCircleComponent
                        className={`w-5 h-5 ${isReceiverComplete ? 'text-[#00899E]' : 'text-[#D9DCE0]'
                          }`}
                      />
                      <span
                        className="font-medium text-[#3F5265]"
                        style={{ fontSize: '16px' }}
                      >
                        To (receiver)
                      </span>
                    </div>
                    {openSection === 'receiver' ? (
                      <IoIosArrowUpComponent size={20} className="text-[#3F5265]" />
                    ) : (
                      <IoIosArrowDownComponent
                        size={20}
                        className="text-[#3F5265]"
                      />
                    )}
                  </div>

                  <p
                    className="text-xs text-[#657584] mt-1"
                    style={{ fontSize: '14px' }}
                  >
                    This defines who will receive the email.
                  </p>

                  {openSection === 'receiver' && (
                    <div className="mt-6 space-y-4">
                      {/* Selection Type Dropdown */}
                      <div>
                        <label
                          className="block text-sm font-normal text-gray-700"
                          style={{ color: '#3F5265', fontSize: '12px' }}
                        >
                          SELECTION TYPE
                        </label>
                        <select
                          value={selectionType}
                          onChange={(e) => handleSelectionTypeChange(e.target.value)}
                          className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                        >
                          <option value="">Select selection type</option>
                          <option value="tags">Tags</option>
                          <option value="segments">Segments</option>
                          <option value="all">All</option>
                          <option value="contactIds">Contact Ids</option>
                        </select>
                      </div>

                      {/* Segments Multi-Select - shown only when selectionType is 'segments' */}
                      {selectionType === 'segments' && (
                        <div>
                          <label
                            className="block text-sm font-normal text-gray-700"
                            style={{ color: '#3F5265', fontSize: '12px' }}
                          >
                            SEGMENTS <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs text-gray-400 mb-1">
                            Select static segments to associate (required)
                          </p>

                          {selectedSegments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {selectedSegments.map(segId => {
                                const seg = allSegments.find(s => s.id === segId);
                                return seg ? (
                                  <span key={segId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                    {seg.name}
                                    <button
                                      type="button"
                                      onClick={() => removeSegment(segId)}
                                      className="hover:text-blue-600"
                                    >
                                      <FiXComponent size={14} />
                                    </button>
                                  </span>
                                ) : (
                                  <span key={segId} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                    Loading...
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          <div className="relative" ref={segmentDropdownRef}>
                            <button
                              type="button"
                              onClick={toggleSegmentDropdown}
                              className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center ${selectedSegments.length === 0 ? 'border-red-500' : 'border-gray-300'
                                }`}
                            >
                              <span className="text-gray-600 text-sm">
                                {selectedSegments.length > 0
                                  ? `${selectedSegments.length} segment${selectedSegments.length > 1 ? 's' : ''} selected`
                                  : 'Select static segments...'}
                              </span>
                              <FiChevronDownComponent className={`transition-transform ${isSegmentDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isSegmentDropdownOpen && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto">
                                <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                                  <input
                                    type="text"
                                    value={segmentSearchTerm}
                                    onChange={(e) => setSegmentSearchTerm(e.target.value)}
                                    placeholder="Search segments..."
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                {loadingSegments ? (
                                  <div className="p-3 text-center text-gray-500">Loading segments...</div>
                                ) : filteredSegments.length === 0 ? (
                                  <div className="p-3 text-center text-gray-500">No static segments available</div>
                                ) : (
                                  filteredSegments.map(seg => {
                                    const isChecked = selectedSegments.includes(seg.id);
                                    return (
                                      <label key={seg.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => toggleSegment(seg.id)}
                                          className="mr-2 w-4 h-4 text-teal-600 rounded"
                                        />
                                        <span className="text-sm">{seg.name}</span>
                                        <span className="ml-2 text-xs text-gray-400">
                                          ({seg.type})
                                        </span>
                                        {isChecked && (
                                          <span className="ml-auto text-xs text-blue-600">✓</span>
                                        )}
                                      </label>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                          <p className="text-xs mt-1">
                            {selectedSegments.length === 0 && (
                              <span className="text-red-500">⚠️ At least 1 segment is required</span>
                            )}
                            {selectedSegments.length > 0 && (
                              <span className="text-blue-600">
                                {selectedSegments.length} segment{selectedSegments.length > 1 ? 's' : ''} selected
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Tags Multi-Select - shown only when selectionType is 'tags' */}
                      {selectionType === 'tags' && (
                        <div>
                          <label
                            className="block text-sm font-normal text-gray-700"
                            style={{ color: '#3F5265', fontSize: '12px' }}
                          >
                            TAGS <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs text-gray-400 mb-1">
                            Select tags to associate (required)
                          </p>

                          {selectedTags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {selectedTags.map(tagName => {
                                const tagObj = allTags.find(t => t.name === tagName);
                                return (
                                  <span key={tagName} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                                    {tagObj ? tagObj.name : tagName}
                                    <button
                                      type="button"
                                      onClick={() => removeTag(tagName)}
                                      className="hover:text-purple-600"
                                    >
                                      <FiXComponent size={14} />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          <div className="relative" ref={tagsDropdownRef}>
                            <button
                              type="button"
                              onClick={toggleTagsDropdown}
                              className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center ${selectedTags.length === 0 ? 'border-red-500' : 'border-gray-300'
                                }`}
                            >
                              <span className="text-gray-600 text-sm">
                                {selectedTags.length > 0
                                  ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''} selected`
                                  : 'Select tags...'}
                              </span>
                              <FiChevronDownComponent className={`transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isTagsDropdownOpen && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto">
                                <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                                  <input
                                    type="text"
                                    value={tagSearchTerm}
                                    onChange={(e) => setTagSearchTerm(e.target.value)}
                                    placeholder="Search tags..."
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                {loadingTags ? (
                                  <div className="p-3 text-center text-gray-500">Loading tags...</div>
                                ) : (
                                  <>
                                    {!isAddingTag ? (
                                      <div
                                        className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                                        onClick={() => {
                                          setIsAddingTag(true);
                                          setTagValidationError('');
                                        }}
                                      >
                                        <span className="text-blue-600 font-medium text-sm flex items-center gap-2">
                                          <span className="text-blue-500 text-lg">+</span> Add Tag
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="px-3 py-2 bg-blue-50 border-b border-blue-200">
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={newTagName}
                                            onChange={(e) => {
                                              setNewTagName(e.target.value);
                                              if (tagValidationError) {
                                                setTagValidationError('');
                                              }
                                            }}
                                            placeholder="Enter tag name..."
                                            className={`flex-1 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${tagValidationError ? 'border-red-500' : 'border-blue-300'
                                              }`}
                                            autoFocus
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                handleCreateTag();
                                              }
                                              if (e.key === 'Escape') {
                                                setIsAddingTag(false);
                                                setNewTagName('');
                                                setTagValidationError('');
                                              }
                                            }}
                                            disabled={isCreatingTag}
                                          />
                                          <button
                                            type="button"
                                            onClick={handleCreateTag}
                                            disabled={isCreatingTag || !newTagName.trim()}
                                            className={`p-1 rounded hover:bg-green-100 ${isCreatingTag || !newTagName.trim() ? 'opacity-50 cursor-not-allowed' : ''
                                              }`}
                                            title="Create tag"
                                          >
                                            <FiCheckComponent size={18} className="text-green-600" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setIsAddingTag(false);
                                              setNewTagName('');
                                              setTagValidationError('');
                                            }}
                                            className="p-1 rounded hover:bg-red-100"
                                            title="Cancel"
                                          >
                                            <FiCrossComponent size={18} className="text-red-600" />
                                          </button>
                                        </div>

                                        {tagValidationError && (
                                          <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                            <span>⚠️</span> {tagValidationError}
                                          </div>
                                        )}

                                        {isCreatingTag && (
                                          <div className="text-xs text-gray-500 mt-1">Creating tag...</div>
                                        )}
                                      </div>
                                    )}

                                    {filteredTags.length === 0 && !isAddingTag ? (
                                      <div className="p-3 text-center text-gray-500">No tags available</div>
                                    ) : (
                                      filteredTags.map(tagObj => {
                                        const isChecked = selectedTags.includes(tagObj.name);
                                        return (
                                          <label key={tagObj.name} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={() => toggleTag(tagObj.name)}
                                              className="mr-2 w-4 h-4 text-teal-600 rounded"
                                            />
                                            <span className="text-sm">{tagObj.name}</span>
                                            {isChecked && (
                                              <span className="ml-auto text-xs text-purple-600">✓</span>
                                            )}
                                          </label>
                                        );
                                      })
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <p className="text-xs mt-1">
                            {selectedTags.length === 0 && (
                              <span className="text-red-500">⚠️ At least 1 tag is required</span>
                            )}
                            {selectedTags.length > 0 && (
                              <span className="text-purple-600">
                                {selectedTags.length} tag{selectedTags.length > 1 ? 's' : ''} selected
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Contacts Multi-Select - shown only when selectionType is 'contactIds' */}
                      {selectionType === 'contactIds' && (
                        <div>
                          <label
                            className="block text-sm font-normal text-gray-700"
                            style={{ color: '#3F5265', fontSize: '12px' }}
                          >
                            CONTACTS <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs text-gray-400 mb-1">
                            Select subscribed contacts to associate (required)
                          </p>

                          {selectedContacts.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {selectedContacts.map(contactId => {
                                const contact = allContacts.find(c => c.id === contactId);
                                return contact ? (
                                  <span key={contactId} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                    {contact.firstName} {contact.lastName}
                                    <button
                                      type="button"
                                      onClick={() => removeContact(contactId)}
                                      className="hover:text-green-600"
                                    >
                                      <FiXComponent size={14} />
                                    </button>
                                  </span>
                                ) : (
                                  <span key={contactId} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                    Loading...
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          <div className="relative" ref={contactsDropdownRef}>
                            <button
                              type="button"
                              onClick={toggleContactsDropdown}
                              className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center ${selectedContacts.length === 0 ? 'border-red-500' : 'border-gray-300'
                                }`}
                            >
                              <span className="text-gray-600 text-sm">
                                {selectedContacts.length > 0
                                  ? `${selectedContacts.length} contact${selectedContacts.length > 1 ? 's' : ''} selected`
                                  : 'Select contacts...'}
                              </span>
                              <FiChevronDownComponent className={`transition-transform ${isContactsDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isContactsDropdownOpen && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto">
                                <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                                  <input
                                    type="text"
                                    value={contactSearchTerm}
                                    onChange={(e) => setContactSearchTerm(e.target.value)}
                                    placeholder="Search contacts..."
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                {loadingContacts ? (
                                  <div className="p-3 text-center text-gray-500">Loading contacts...</div>
                                ) : filteredContacts.length === 0 ? (
                                  <div className="p-3 text-center text-gray-500">No contacts available</div>
                                ) : (
                                  filteredContacts.map(contact => {
                                    const isChecked = selectedContacts.includes(contact.id);
                                    return (
                                      <label key={contact.id} className="flex flex-col px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                                        <div className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleContact(contact.id)}
                                            className="mr-2 w-4 h-4 text-teal-600 rounded flex-shrink-0"
                                          />
                                          <span className="text-sm font-medium">
                                            {contact.firstName} {contact.lastName}
                                          </span>
                                          {isChecked && (
                                            <span className="ml-auto text-xs text-green-600">✓</span>
                                          )}
                                        </div>
                                        <span className="text-xs text-gray-400 ml-6">
                                          {contact.email}
                                        </span>
                                      </label>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                          <p className="text-xs mt-1">
                            {selectedContacts.length === 0 && (
                              <span className="text-red-500">⚠️ At least 1 contact is required</span>
                            )}
                            {selectedContacts.length > 0 && (
                              <span className="text-green-600">
                                {selectedContacts.length} contact{selectedContacts.length > 1 ? 's' : ''} selected
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Show message when 'All' is selected */}
                      {selectionType === 'all' && (
                        <div className="text-sm text-gray-500 italic">
                          All contacts will be selected
                        </div>
                      )}

                      {/* Show message when default is selected */}
                      {selectionType === '' && (
                        <div className="text-sm text-gray-500 italic">
                          Please select a selection type to choose your audience
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Subject Section */}
                <div
                  className={`border rounded-lg p-5 relative ${openSection === 'subject' ? 'bg-[#E0F8FB]' : ''
                    }`}
                >
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleSection('subject')}
                  >
                    <div className="flex items-center gap-2">
                      <IoIosCheckmarkCircleComponent
                        className={`w-5 h-5 ${isSubjectComplete ? 'text-[#00899E]' : 'text-[#D9DCE0]'
                          }`}
                      />
                      <span
                        className="font-medium text-[#3F5265]"
                        style={{ fontSize: '16px' }}
                      >
                        Subject <span className="text-gray-400 text-xs">(Optional)</span>
                      </span>
                    </div>
                    {openSection === 'subject' ? (
                      <IoIosArrowUpComponent size={20} className="text-[#3F5265]" />
                    ) : (
                      <IoIosArrowDownComponent
                        size={20}
                        className="text-[#3F5265]"
                      />
                    )}
                  </div>

                  <p
                    className="text-xs text-[#657584] mt-1"
                    style={{ fontSize: '14px' }}
                  >
                    Fundamental information about the email itself.
                  </p>

                  {openSection === 'subject' && (
                    <div className="mt-6">
                      <label
                        className="block text-sm font-normal text-gray-700"
                        style={{ color: '#3F5265', fontSize: '12px' }}
                      >
                        SUBJECT <span className="text-gray-400 text-xs">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Enter subject line (optional)"
                        className="block w-full border rounded-lg p-2"
                        style={{ marginTop: '3px' }}
                        disabled={isSaving}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* buttons */}
      <div className="flex justify-between mt-6">
        <button
          onClick={() => {
            handleBack();
          }}
          className="bg-[#00A4BD] text-white px-6 py-2 rounded-md font-medium hover:bg-[#00899E] transition-colors"
          style={{ fontSize: '14px' }}
          disabled={isSaving}
        >
          ← Back
        </button>

        <div className="flex gap-3">
          <button
            onClick={handleSaveAsDraft}
            disabled={
              isSaving ||
              (isEditMode ? !isUpdateEnabled : !isDraftEnabled)
            }
            className={`px-6 py-2 rounded-md font-medium transition-colors ${!isSaving && (isEditMode ? isUpdateEnabled : isDraftEnabled)
              ? 'bg-white border border-[#00A4BD] text-[#00A4BD] hover:bg-[#E0F8FB]'
              : 'bg-white border border-gray-400 text-gray-400 cursor-not-allowed'
              }`}
            style={{ fontSize: '14px' }}
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                  />
                </svg>
                Saving...
              </span>
            ) : (
              isEditMode ? 'Update Campaign' : 'Save as Draft'
            )}
          </button>
          {showNextButton && (
            <button
              onClick={handleNextWithValidation}
              disabled={!isNextEnabled}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${isNextEnabled
                ? 'bg-[#00A4BD] text-white hover:bg-teal-600'
                : 'bg-gray-300 text-white cursor-not-allowed'
                }`}
              style={{ fontSize: '14px' }}
            >
              Next →
            </button>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default SetContext;